import type { MediaManagerTemplate } from 'modules/core/domain/interfaces/supabase/media-manager/entity';
import { useChampionshipMediaSettingsQuery } from 'modules/championship/store/championship-media-api';
import { MediaManagerTemplateGroup } from 'modules/manager/domain/interfaces/media-manager-template-group';
import {
  useMediaManagerPresetTemplateGroupsQuery,
  useMediaManagerTemplateGroupQuery,
  useMediaManagerTemplateGroupsQuery,
} from 'modules/manager/store/media-manager-api';
import { BUILT_IN_VIDEO_SET_IDS, BuiltInVideoSetId } from 'modules/match-video/domain/constants';
import { ReviewClipKind } from 'modules/match-video/domain/interfaces';
import { pickIntroTemplate, pickPlateTemplates } from 'modules/match-video/domain/utils';
import { useMemo, useState } from 'react';

/** A set the review is drawn with: a built-in one (drawn by code) or a media manager template group. */
export type VideoSetChoice = { kind: 'built-in'; id: BuiltInVideoSetId } | { kind: 'group'; id: number };

export interface VideoTemplateGroups {
  /** Groups of the championship's organizer with at least one template. */
  own: MediaManagerTemplateGroup[];
  /** Global presets («Общие шаблоны») with at least one template. */
  presets: MediaManagerTemplateGroup[];
  /** The lists are loading for the first time. */
  isLoading: boolean;
  isError: boolean;
  /** The chosen template group; `null` — a built-in set is chosen, or nothing yet. */
  selectedId: number | null;
  /** The chosen built-in set; `null` — a template group is chosen, or nothing yet. */
  builtInId: BuiltInVideoSetId | null;
  select: (choice: VideoSetChoice) => void;
  /** The chosen group's templates are loading. */
  isGroupLoading: boolean;
  isGroupError: boolean;
  /** The intro of the chosen group; `null` — it has no match template (or is still loading). */
  intro: MediaManagerTemplate | null;
  /** Plate templates of the chosen group by clip kind (`VIDEO_GOAL_PLATE`, `VIDEO_DANGER_PLATE`); empty while loading. */
  plates: Partial<Record<ReviewClipKind, MediaManagerTemplate>>;
  refetch: () => void;
}

const withTemplates = (groups: MediaManagerTemplateGroup[] | undefined) => (groups ?? []).filter((group) => group.templatesCount > 0);

const DEFAULT_BUILT_IN = BUILT_IN_VIDEO_SET_IDS[0];

const NO_PLATES: Partial<Record<ReviewClipKind, MediaManagerTemplate>> = {};

/**
 * The set a review is drawn with. By default — the set assigned to the championship on «Шаблоны» (the same look as
 * its images) when it has an intro template, else the built-in «Ночной стадион». The lists carry no templates, so
 * whether a group has an intro is known only after the group itself is loaded: the assigned one is shown while it
 * loads and gives way to the built-in set if it turns out to have none.
 *
 * The intro of a group is its `VIDEO_INTRO`, or a match template in a set made before the video types
 * (`pickIntroTemplate`); its plates are the `VIDEO_GOAL_PLATE` / `VIDEO_DANGER_PLATE` templates (`pickPlateTemplates`).
 */
export function useVideoTemplateGroups(championshipId: number, organizerId: number): VideoTemplateGroups {
  const settings = useChampionshipMediaSettingsQuery(championshipId);
  const ownQuery = useMediaManagerTemplateGroupsQuery({ tournamentId: organizerId });
  const presetsQuery = useMediaManagerPresetTemplateGroupsQuery();
  const [chosen, setChosen] = useState<VideoSetChoice | null>(null);

  const own = useMemo(() => withTemplates(ownQuery.data), [ownQuery.data]);
  const presets = useMemo(() => withTemplates(presetsQuery.data), [presetsQuery.data]);

  const isLoading = (settings.isLoading || ownQuery.isLoading || presetsQuery.isLoading) && !(ownQuery.data && presetsQuery.data);
  const assignedId = settings.data?.templateGroup?.id;
  const defaultGroupId =
    !isLoading && assignedId !== undefined && [...own, ...presets].some((group) => group.id === assignedId) ? assignedId : null;

  const groupId = chosen ? (chosen.kind === 'group' ? chosen.id : null) : defaultGroupId;
  const groupQuery = useMediaManagerTemplateGroupQuery(groupId ?? 0, { skip: groupId === null });
  const groupLoaded = groupId !== null && groupQuery.data?.id === groupId;
  const intro = useMemo(
    () => (groupLoaded && groupQuery.data ? pickIntroTemplate(groupQuery.data.templates) : null),
    [groupLoaded, groupQuery.data],
  );
  const plates = useMemo(
    () => (groupLoaded && groupQuery.data ? pickPlateTemplates(groupQuery.data.templates) : NO_PLATES),
    [groupLoaded, groupQuery.data],
  );

  // The assigned group is only a default: without an intro it gives way to the built-in set. A chosen one stays.
  const defaultWithoutIntro = !chosen && groupLoaded && !groupQuery.isFetching && !intro;
  const selectedId = defaultWithoutIntro ? null : groupId;
  const builtInId = chosen?.kind === 'built-in' ? chosen.id : !chosen && !isLoading && selectedId === null ? DEFAULT_BUILT_IN : null;

  return {
    own,
    presets,
    isLoading,
    isError: ownQuery.isError || presetsQuery.isError,
    selectedId,
    builtInId,
    select: setChosen,
    isGroupLoading: selectedId !== null && groupQuery.isFetching && !groupLoaded,
    isGroupError: selectedId !== null && groupQuery.isError,
    intro: selectedId !== null ? intro : null,
    plates: selectedId !== null ? plates : NO_PLATES,
    refetch: () => {
      ownQuery.refetch();
      presetsQuery.refetch();
      if (groupId !== null) groupQuery.refetch();
    },
  };
}
